import React from "react";
import styles from "./List.module.css";

type TextSize = "xxs" | "xs" | "s" | "m" | "l" | "xl" | "xxl";
type LineHeight = "tight" | "snug" | "normal" | "relaxed" | "loose";
type ListType = "ul" | "ol";
type ListStyleType =
  | "disc"
  | "circle"
  | "square"
  | "decimal"
  | "decimal-leading-zero"
  | "lower-alpha"
  | "upper-alpha"
  | "lower-roman"
  | "upper-roman"
  | "none"
  | "dash";

export interface ListProps {
  /** Ordered content rendered as one list item per entry. */
  items: React.ReactNode[];
  /** Semantic list element. @default "ul" */
  as?: ListType;
  /** Additional class name applied to the list element. */
  className?: string;
  /** Tokenized text size. @default "m" */
  size?: TextSize;
  /** Optional tokenized line height. */
  lineHeight?: LineHeight;
  /**
   * Inline style merged before the computed marker style: when listStyleType or
   * striped resolves a marker, it overrides style.listStyleType.
   */
  style?: React.CSSProperties;
  /**
   * Native marker style or the custom dash treatment. "none" and "dash" blank
   * the native marker with a single-space string, not CSS none, so Safari and
   * VoiceOver keep list semantics.
   */
  listStyleType?: ListStyleType;
  /** Vertical spacing between items. @default "normal" */
  spacing?: "compact" | "normal" | "relaxed";
  /** Optional ARIA role override for specialized list semantics. */
  role?: string;
  /**
   * Alternate row backgrounds for long lists of multi-line entries; rows get
   * padding and no markers unless listStyleType asks for them. This includes
   * as="ol", so a striped ordered list hides its numbers unless listStyleType
   * is set.
   * @default false
   */
  striped?: boolean;
}

const sizeClassMap: Record<TextSize, string> = {
  xxs: styles["textXXS"] || "",
  xs: styles["textXS"] || "",
  s: styles["textS"] || "",
  m: styles["textM"] || "",
  l: styles["textL"] || "",
  xl: styles["textXL"] || "",
  xxl: styles["textXXL"] || "",
};

const lineHeightClassMap: Record<LineHeight, string> = {
  tight: styles["lineHeightTight"] || "",
  snug: styles["lineHeightSnug"] || "",
  normal: styles["lineHeightNormal"] || "",
  relaxed: styles["lineHeightRelaxed"] || "",
  loose: styles["lineHeightLoose"] || "",
};

const spacingClassMap: Record<string, string> = {
  compact: styles["spacingCompact"] || "",
  normal: styles["spacingNormal"] || "",
  relaxed: styles["spacingRelaxed"] || "",
};

/**
 * List component.
 */
export const List = React.forwardRef<
  HTMLUListElement | HTMLOListElement,
  ListProps
>(function List(
  {
    items,
    as = "ul",
    className = "",
    size = "m",
    lineHeight,
    style,
    listStyleType,
    spacing = "normal",
    role,
    striped = false,
  },
  ref,
) {
  // Polymorphic tag (ul/ol): widen to ElementType so the forwarded ref types
  // cleanly against both list elements.
  const Tag = as as React.ElementType;
  const sizeClass = sizeClassMap[size] || "";
  const lineHeightClass = lineHeight
    ? lineHeightClassMap[lineHeight] || ""
    : "";
  const spacingClass = spacingClassMap[spacing] || "";

  // Striped rows are the item boundary; a marker beside each row is noise.
  const effectiveListStyleType =
    listStyleType ?? (striped ? "none" : undefined);
  // "none" and "dash" both blank the native marker with a single-space string
  // rather than CSS none: Safari/VoiceOver drop list semantics on
  // list-style-type:none. The dash itself is drawn by .markerDash li::before
  // so it can hang in the li padding.
  const resolvedListStyleType =
    effectiveListStyleType === "none" || effectiveListStyleType === "dash"
      ? ('" "' as ListStyleType)
      : effectiveListStyleType;

  const combinedStyle = {
    ...style,
    ...(resolvedListStyleType && { listStyleType: resolvedListStyleType }),
  };

  return (
    <Tag
      ref={ref}
      className={`${styles.list} ${sizeClass} ${lineHeightClass} ${spacingClass} ${
        effectiveListStyleType === "dash" ? styles.markerDash : ""
      } ${striped ? styles.striped : ""} ${className}`
        .replace(/\s+/g, " ")
        .trim()}
      style={combinedStyle}
      role={role}
    >
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </Tag>
  );
});

List.displayName = "List";

export default List;
